import React from "react";
import JobSelect from "./JobSelect";
import {
  RACI_ROLES,
  ROLE_LABELS,
  PARTICIPANT_CURRENCY,
} from "../../utils/whatif/whatIfNormalize";
import { canAddRole, ROLE_SLOT_LIMIT } from "../../utils/whatif/whatIfScenario";
import { buildJobOptions } from "../../utils/whatif/whatIfJobOptions";
import {
  formatAmount,
  formatDurationShort,
  formatPercent,
  formatRate,
} from "../../utils/whatif/whatIfFormat";

const ALLOCATION_MIN = 0;
const ALLOCATION_MAX = 100;

const clampAllocation = (value) => {
  if (value === "") return "";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "";
  return Math.min(ALLOCATION_MAX, Math.max(ALLOCATION_MIN, parsed));
};

const rowModifier = (entry) => {
  if (entry.added) return " wif-slot-row--added";
  if (entry.changed) return " wif-slot-row--changed";
  return "";
};

const SlotRow = ({
  entry,
  jobOptions,
  timeUnit,
  currency,
  rates,
  onJobChange,
  onAllocationChange,
  onRemove,
}) => {
  const { slot, minutes, costUsd, rateKnown, unassigned, candidate } = entry;
  const jobLabel = unassigned ? "" : slot.jobName;
  const noAlternatives = jobOptions.length === 0;

  return (
    <div className={`wif-slot-row${rowModifier(entry)}`}>
      <div className="wif-slot-job">
        <JobSelect
          id={`wif-job-${slot.id}`}
          label={jobLabel}
          options={jobOptions}
          disabled={noAlternatives && !unassigned}
          onSelect={(option) => onJobChange(slot.id, option)}
        />
        {/* Reachable only when the company list is empty or failed to load —
            otherwise every job in the company is an option. */}
        {noAlternatives && (
          <div className="wif-slot-was">No other job available for this role</div>
        )}
        {entry.added && !unassigned && (
          <div className="wif-slot-was wif-slot-was--added">Added</div>
        )}
        {entry.added && unassigned && (
          <div className="wif-slot-was wif-slot-was--todo">Select a job</div>
        )}
        {!entry.added && entry.changed && (
          <div className="wif-slot-was">was {slot.original.jobName}</div>
        )}
        {/* A job picked from outside the role's eligible set, or with no skill
            overlap at all, is allowed but never silent. */}
        {!unassigned && candidate?.flagged && (
          <div className="wif-slot-was wif-slot-was--flagged">
            {candidate.eligible
              ? "No skill match for this role"
              : "Not listed as eligible for this role"}
          </div>
        )}
        {!unassigned && !candidate?.flagged && candidate?.skillMatchPercent != null && (
          <div className="wif-slot-was">
            {formatPercent(candidate.skillMatchPercent)} skill match
          </div>
        )}
      </div>

      <div className="wif-slot-alloc">
        <input
          type="number"
          className="form-input"
          min={ALLOCATION_MIN}
          max={ALLOCATION_MAX}
          step="0.01"
          value={slot.allocationPercent}
          aria-label={`${ROLE_LABELS[slot.role]} allocation percentage`}
          onChange={(event) =>
            onAllocationChange(slot.id, clampAllocation(event.target.value))
          }
        />
        <span className="wif-slot-suffix">%</span>
      </div>

      <div className="wif-slot-figure">
        <span className="wif-slot-figure-label">Effort</span>
        <span className="wif-slot-figure-value">
          {formatDurationShort(minutes, timeUnit)}
        </span>
      </div>

      <div className="wif-slot-figure">
        <span className="wif-slot-figure-label">Cost</span>
        <span className="wif-slot-figure-value">
          {unassigned ? (
            <span className="wif-slot-muted">—</span>
          ) : rateKnown ? (
            formatAmount(costUsd, PARTICIPANT_CURRENCY, currency, rates)
          ) : (
            <span className="wif-slot-norate">no rate</span>
          )}
        </span>
        {!unassigned && rateKnown && (
          <span className="wif-slot-figure-sub">
            {formatRate(
              slot.hourlyRateUsd,
              PARTICIPANT_CURRENCY,
              currency,
              rates,
              timeUnit,
            )}
          </span>
        )}
      </div>

      {/* Existing staffing cannot be dropped, only reassigned or re-weighted, so
          the control is present solely on assignments added in this scenario. */}
      {entry.added ? (
        <button
          type="button"
          className="wif-slot-remove"
          title={`Remove this ${ROLE_LABELS[slot.role]} assignment`}
          aria-label={`Remove ${slot.jobName || "unassigned"} from ${ROLE_LABELS[slot.role]}`}
          onClick={() => onRemove(slot.id)}
        >
          ×
        </button>
      ) : (
        <span aria-hidden="true" />
      )}
    </div>
  );
};

const RoleGroup = ({ role, entries, task, onAddSlot, children }) => {
  const addable = canAddRole(task.slots, role);
  const singular = ROLE_SLOT_LIMIT[role] === 1;

  return (
    <div className="wif-role-group">
      <div className="wif-role-group-head">
        <span className={`wif-slot-role wif-slot-role--${role}`}>
          <span className="wif-slot-role-key">{role}</span>
          <span className="wif-slot-role-name">{ROLE_LABELS[role]}</span>
        </span>

        {addable ? (
          <button
            type="button"
            className="wif-link-btn"
            onClick={() => onAddSlot(task.taskId, role)}
          >
            + Add {ROLE_LABELS[role]}
          </button>
        ) : (
          singular && <span className="wif-role-note">One per task</span>
        )}
      </div>

      {entries.length ? (
        <div className="wif-slot-list">{children}</div>
      ) : (
        <p className="wif-role-empty">No {ROLE_LABELS[role]} assigned.</p>
      )}
    </div>
  );
};

const TaskCard = ({
  entry,
  companyJobsById,
  timeUnit,
  currency,
  rates,
  onJobChange,
  onAllocationChange,
  onAddSlot,
  onRemoveSlot,
  onResetTask,
}) => {
  const { task, slots, minutes, costUsd, changedCount, eliminated } = entry;
  const costDelta = costUsd - task.originalCostUsd;
  // From the entry, not the task: a duration edit in the Tasks tab rescales the
  // base every allocation here is a percentage of.
  const baseMinutes = entry.baseMinutes;

  // Eligibility is scoped per RACI role, so each role gets its own option list —
  // the company's other jobs are appended to each, labelled as ineligible.
  const optionsByRole = React.useMemo(
    () =>
      RACI_ROLES.reduce((result, role) => {
        result[role] = buildJobOptions(
          task.eligibleJobsByRole?.[role],
          companyJobsById,
        );
        return result;
      }, {}),
    [task.eligibleJobsByRole, companyJobsById],
  );

  const hasAnyCandidates = RACI_ROLES.some(
    (role) => optionsByRole[role].length > 0,
  );

  // Jobs already holding this role on this task. Offering one of them again
  // would either be a no-op (the row's own job) or create a duplicate — the same
  // job listed twice as Consulted on one task.
  const takenByRole = React.useMemo(() => {
    const taken = {};
    RACI_ROLES.forEach((role) => {
      taken[role] = new Set();
    });
    slots.forEach(({ slot }) => {
      if (slot.jobId != null && slot.jobId !== "") {
        taken[slot.role]?.add(String(slot.jobId));
      }
    });
    return taken;
  }, [slots]);

  const availableFor = (role) =>
    optionsByRole[role].filter((option) => !takenByRole[role].has(option.value));

  const candidateFor = (slot) =>
    optionsByRole[slot.role].find(
      (option) => String(option.jobId) === String(slot.jobId),
    ) || null;

  return (
    <div
      className={`wif-scenario-task${changedCount ? " wif-scenario-task--changed" : ""}${
        eliminated ? " wif-scenario-task--eliminated" : ""
      }`}
    >
      <div className="wif-scenario-task-head">
        <div className="wif-raci-task-heading">
         
          <span className="wif-raci-task-name">{task.taskName}</span>
          {eliminated && <span className="wif-eliminated-badge">Eliminated</span>}
        </div>

        <div className="wif-scenario-task-meta">
          <span>
            Base {formatDurationShort(baseMinutes, timeUnit)} ·{" "}
            {formatDurationShort(minutes, timeUnit)} attributed ·{" "}
            {formatAmount(costUsd, PARTICIPANT_CURRENCY, currency, rates)}
          </span>
          {changedCount > 0 && (
            <>
              <span
                className={`wif-delta wif-delta--${costDelta > 0 ? "up" : costDelta < 0 ? "down" : "flat"}`}
              >
                {costDelta >= 0 ? "+" : "−"}
                {formatAmount(
                  Math.abs(costDelta),
                  PARTICIPANT_CURRENCY,
                  currency,
                  rates,
                )}
              </span>
              <button
                type="button"
                className="wif-link-btn"
                onClick={() => onResetTask(task.taskId)}
              >
                Reset task
              </button>
            </>
          )}
        </div>
      </div>

      {eliminated && (
        <p className="wif-role-note wif-role-note--block">
          Classified NVA on the Tasks tab, so this task and everything staffed
          against it are excluded from the to-be totals.
        </p>
      )}

      {!eliminated && !hasAnyCandidates && (
        <p className="wif-role-note wif-role-note--block">
          No jobs are available to reassign to on this task — the analysis listed
          none as eligible and the company's job list could not be loaded.
        </p>
      )}

      <div className="wif-role-groups">
        {RACI_ROLES.map((role) => {
          const entries = slots.filter(
            (slotEntry) => slotEntry.slot.role === role,
          );

          return (
            <RoleGroup
              key={role}
              role={role}
              entries={entries}
              task={task}
              onAddSlot={onAddSlot}
            >
              {entries.map((slotEntry) => (
                <SlotRow
                  key={slotEntry.slot.id}
                  entry={{
                    ...slotEntry,
                    candidate: candidateFor(slotEntry.slot),
                  }}
                  jobOptions={availableFor(role)}
                  timeUnit={timeUnit}
                  currency={currency}
                  rates={rates}
                  onJobChange={(slotId, option) =>
                    onJobChange(task.taskId, slotId, option)
                  }
                  onAllocationChange={(slotId, value) =>
                    onAllocationChange(task.taskId, slotId, value)
                  }
                  onRemove={(slotId) => onRemoveSlot(task.taskId, slotId)}
                />
              ))}
            </RoleGroup>
          );
        })}
      </div>
    </div>
  );
};

// The "Job Allocation" tab: who holds each RACI role on each task, and for how
// much of it. Mirrors TasksTab's shape so the two tabs plug in identically.
const JobAllocationTab = ({
  scenario,
  companyJobsById,
  companyJobsError,
  timeUnit,
  currency,
  rates,
  onJobChange,
  onAllocationChange,
  onAddSlot,
  onRemoveSlot,
  onResetTask,
}) => (
  <>
    <p className="muted">
      Reassign the job holding each RACI role and adjust its time allocation.
      Every job in the company is offered, with the ones the analysis rated for
      that role listed first — picking outside them is allowed and flagged.
      Effort and cost update as you edit.
    </p>

    {companyJobsError && (
      <ul className="whatif-warnings">
        <li>{companyJobsError} Only the roles' eligible jobs are listed.</li>
      </ul>
    )}

    <div className="wif-scenario-tasks">
      {scenario.tasks.map((entry) => (
        <TaskCard
          key={entry.task.taskId}
          entry={entry}
          companyJobsById={companyJobsById}
          timeUnit={timeUnit}
          currency={currency}
          rates={rates}
          onJobChange={onJobChange}
          onAllocationChange={onAllocationChange}
          onAddSlot={onAddSlot}
          onRemoveSlot={onRemoveSlot}
          onResetTask={onResetTask}
        />
      ))}
    </div>
  </>
);

export default JobAllocationTab;
